import type { Components } from '@/api/generated/api';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import type { TViewedSchoolClass } from '@/schoolClasses/useGroup';
import { computed, inject, provide, toValue, type ComputedRef, type InjectionKey, type MaybeRefOrGetter } from 'vue';

const viewedSchoolClassKey: InjectionKey<ComputedRef<TViewedSchoolClass>> = Symbol('viewedSchoolClass');

/**
 * Declares the class/group nested components act on, so it does not have to be threaded through props.
 * Needed wherever it cannot be read from the routed class: creation wizards (the class does not exist
 * yet, so only its type is known) and editors for a class other than the routed one. Provided per
 * component instance, so it cannot distinguish rows of a list — those pass their own class instead.
 */
export const provideViewedSchoolClass = (schoolClass: MaybeRefOrGetter<TViewedSchoolClass>): void => {
  provide(
    viewedSchoolClassKey,
    computed(() => toValue(schoolClass)),
  );
};

/** The provided class itself, for composables resolving more of it than its type. */
export const injectViewedSchoolClass = (): ComputedRef<TViewedSchoolClass> | null => inject(viewedSchoolClassKey, null);

/**
 * The class/group the surrounding UI acts on, most specific source first:
 * 1. the passed class — the only source that can differ per row, for components listing access
 *    rights across several classes at once (ClassAccessRightsEditor, bulk invite),
 * 2. the nearest `provideViewedSchoolClass`,
 * 3. the routed class, which is correct for everything acting on the class the user navigated to.
 */
export const useSchoolClassType = (
  schoolClass?: MaybeRefOrGetter<TViewedSchoolClass | null | undefined>,
): ComputedRef<Components.Schemas.SchoolClassType> => {
  const providedSchoolClass = injectViewedSchoolClass();
  const { currentClassType } = useCurrentClass();

  return computed(() => toValue(schoolClass)?.type ?? providedSchoolClass?.value.type ?? currentClassType.value);
};
